import React from "react";
import { usePlayer } from "@/context/PlayerContext";
import { Play, Pause, SkipForward, Volume2, Download } from "lucide-react";
import { coverUrl, downloadTrackUrl, trackSrc, detailPathFor } from "@/lib/api";
import { Link } from "react-router-dom";
import { Waveform } from "@/components/Waveform";

const fmt = (s) => {
  if (!s || Number.isNaN(s)) return "0:00";
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${sec.toString().padStart(2, "0")}`;
};

export default function PersistentPlayer() {
  const p = usePlayer();
  if (!p || !p.current) return null;
  const t = p.current;
  const isPaid = (t.price || 0) > 0;
  const downloadHref = t.download_url || downloadTrackUrl(t.id);

  return (
    <div data-testid="persistent-player"
      className="fixed bottom-0 left-0 right-0 z-50 h-24 bg-black/90 backdrop-blur-xl border-t border-white/10">
      <div className="h-full max-w-7xl mx-auto px-4 sm:px-6 flex items-center gap-3 sm:gap-5">
        <Link to={detailPathFor(t.pista_id ? { ...t, id: t.pista_id } : t)} data-testid="player-track-link"
          className="flex items-center gap-3 min-w-0 w-40 sm:w-60 shrink-0">
          <div className="w-12 h-12 rounded-md overflow-hidden bg-neutral-900 border border-white/10 shrink-0">
            {t.cover_path ? (
              <img src={coverUrl(t.cover_path, 160)} alt={t.title} className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full bg-gradient-to-br from-red-900/40 to-black"></div>
            )}
          </div>
          <div className="min-w-0">
            <div data-testid="player-title" className="text-sm font-bold text-white truncate">{t.title}</div>
            <div className="text-xs text-white/80 truncate">{t.artist}</div>
          </div>
        </Link>

        <div className="flex items-center gap-2 shrink-0">
          <button data-testid="player-play-pause-button" onClick={p.togglePlay}
            className="w-11 h-11 rounded-full btn-red flex items-center justify-center text-white">
            {p.isPlaying ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5 ml-0.5" />}
          </button>
          <button data-testid="player-next-button" onClick={p.next} className="text-white/60 hover:text-white p-1">
            <SkipForward className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 min-w-0 h-14 flex items-center gap-3">
          <span className="hidden sm:block font-mono text-xs text-white/50 w-10 text-right shrink-0" data-testid="player-time">{fmt(p.progress)}</span>
          <div className="flex-1 h-full min-w-0">
            <Waveform src={trackSrc(t)} progress={p.progress} duration={p.duration} onSeek={p.seek}
              previewSec={null} previewStart={Number(t.preview_start) || 0} />
          </div>
        </div>

        <div className="hidden md:flex items-center gap-3 shrink-0">
          {t.bpm && (
            <span className="font-mono text-[10px] text-sky-400 border border-sky-800/50 bg-sky-950/40 px-2 py-0.5 rounded-full uppercase tracking-widest">
              {t.bpm} BPM
            </span>
          )}
          <Volume2 className="w-4 h-4 text-white/50" />
          <input data-testid="player-volume-slider" type="range" min="0" max="1" step="0.01" value={p.volume}
            onChange={(e) => p.setVolume(parseFloat(e.target.value))} className="w-20 accent-red-600" />
          {!isPaid && (
            <a data-testid={`player-download-${t.id}`} href={downloadHref} className="p-2 text-white/70 hover:text-red-500" title="Descargar">
              <Download className="w-4 h-4" />
            </a>
          )}
        </div>
      </div>
    </div>
  );
}
